import { Injectable } from '@angular/core';
import { Observable, map, filter } from 'rxjs';
import { IArea } from '@shared/features/gist/interfaces/area.interface';
import { HttpService } from '@shared/services/http.service';
import { IRequestParams } from '@shared/interfaces/request-params.interface';
import {
  ApplicationList,
  ApplicationRequest,
  ApplicationView,
  LandDetail,
  NearbyLand,
} from '../interfaces/application.interface';
import { Brigade } from '../interfaces/brigade.interface';
import { IPageable } from '@shared/interfaces/pageable.interface';
import { IStatusCounts } from '@features/applications/interfaces/tab.interface';
import { removeEmptyProperties } from '@shared/utils/customFilterProperties';
import { ITechniques } from '@features/admin/interfaces/technique.interface';
import { IName } from '@shared/interfaces/name.interface';
import { APPLICATION_STATUSES } from '@features/applications/constants/application-status.constant';

@Injectable({
  providedIn: 'root',
})
export class ApplicationApiService extends HttpService {
  getAreas(regionId: string, districtId: string): Observable<IArea[]> {
    return this.get('api/areas', { params: { district_id: districtId } });
  }

  getNeighborhoods(param: { area_id?: string; district_id?: string }): Observable<IArea[]> {
    const filtered = removeEmptyProperties(param);
    return this.get('api/neighborhoods', { params: filtered });
  }

  getBranches(): Observable<IArea[]> {
    return this.get('api/branches/find_with_districts');
  }

  getFuelType(): Observable<IName[]> {
    return this.get('api/fuel_types');
  }

  getTechniqueTypes(): Observable<IName[]> {
    return this.get('api/technique_types');
  }

  getOriginTypes(): Observable<IArea[]> {
    return this.get('api/origin-types');
  }

  getLocationTypes(): Observable<IArea[]> {
    return this.get('api/location-types');
  }

  getPlaceTypes(): Observable<IArea[]> {
    return this.get('api/place-types');
  }

  getUsers(): Observable<IName[]> {
    return this.get('api/users');
  }

  createApplication(application: ApplicationRequest): Observable<ApplicationRequest> {
    return this.post('api/applications', application);
  }

  updateApplication(application: ApplicationRequest, id: string): Observable<ApplicationRequest> {
    return this.put(`api/applications/${id}`, application);
  }

  getApplications(params: IRequestParams = {}): Observable<IPageable<ApplicationList[]>> {
    const searchParams = {
      ...params,
      ...params.filter,
    };
    delete searchParams.filter;
    return this.get('api/applications', { params: searchParams });
  }

  getApplicationsByComplex(complex_id: string, params: IRequestParams = {}): Observable<IPageable<ApplicationList[]>> {
    const searchParams = {
      ...params,
      ...params.filter,
      complex_id: !!complex_id ? complex_id : null,
    };
    delete searchParams.filter;
    return this.get('api/applications/by-complex', { params: searchParams });
  }

  getApplication(id: string): Observable<ApplicationView> {
    return this.get(`api/applications/${id}`);
  }

  getApplicationCounts(params: IRequestParams = {}): Observable<IStatusCounts[]> {
    const searchParams = {
      ...params,
    };
    delete searchParams.status;
    return this.get(`api/applications/count-by-status`, { params: searchParams });
  }

  getRegionsOrDistricts(region_id?: string) {
    return this.get<any>('api/regions').pipe(
      filter(item => {
        return region_id ? item.parent_id === region_id : true;
      }),
      map(w =>
        w.map(r => {
          if (!r.location) console.log(r, 'no location');
          return { ...r, center: r.location ? [r.location.lat, r.location.long] : [] } as IArea;
        })
      )
    );
  }

  getBrigades(body?: any) {
    return this.get<Brigade[]>('api/brigades', body);
  }

  getOrganizations(body?: any) {
    return this.get<IName[]>('api/organizations', body);
  }

  getActiveBrigades(params?: any) {
    return this.get<Brigade[]>('api/brigades/active', { params: params });
  }

  getSpecTechniques(url = '/find_special', params?: any) {
    return this.get<ITechniques[]>(`api/techniques${url}`, { params: params });
  }

  sendToBrigade(body) {
    const { application_id, ...values } = body;
    return this.put(`api/applications/send_to_branch/${application_id}`, values);
  }

  updateStatus(application_id: string, body: { status: APPLICATION_STATUSES }) {
    return this.put(`api/applications/set_application_status/${application_id}`, body);
  }

  getBrigadeApplications(brigade_id: string) {
    return this.get('api/applications_brigades', { params: { brigade_id } });
  }

  getApplicationTechniques(technique_id: string) {
    return this.get('api/application_techniques', { params: { technique_id } });
  }

  assignBrigade(body) {
    const { main_application_brigade_id: id, ...data } = body;
    if (id && data.is_main) {
      return this.put(`api/applications_brigades/${id}`, data);
    }
    return this.post('api/applications_brigades', data);
  }

  assignSpecTechnique(body) {
    return this.post('api/application_techniques', body);
  }

  deleteAssignedApplicationBrigade(id: string) {
    return this.delete(`api/applications_brigades/${id}`);
  }

  deleteAssignedSpecTechnique(id: string) {
    return this.delete(`api/application_techniques/${id}`);
  }

  brigadeCoordinates(params) {
    return this.get('api/brigades/coordinates', { params });
  }

  assignBrigadeAtWork(body) {
    const { brigade_id: id, ...params } = body;
    return this.put(`api/brigades/technique/${id}`, params);
  }

  getApplicationHistory(application_id) {
    return this.get('api/application_histories', { params: { application_id } });
  }

  getTechniquesWithCoordinates() {
    return this.get('api/techniques/find_all_by_user');
  }

  getBrigadeHistory<T>(params) {
    return this.get<T>('api/brigades_histories', { params });
  }

  acceptApplication(id: string, body: { comment: string; budget: number; water_amount: number; sale?: number }) {
    return this.put(`api/applications/${id}/accept`, body);
  }

  sendToSales(id: string, body: { comment: string; budget: number; water_amount: number }) {
    return this.put(`api/applications/${id}/sales`, body);
  }

  brigadeOnWay(id: string, body: { comment: string; file_ids: string[] }) {
    return this.put(`api/applications/${id}/brigade-on-way`, body);
  }

  startWork(id: string, body: { comment: string; file_ids: string[] }) {
    return this.put(`api/applications/${id}/start`, body);
  }

  finishApplication(id: string, body: { comment: string; file_ids: string[] }) {
    return this.put(`api/applications/${id}/finish`, body);
  }

  rejectApplication(id: string, body: { comment: string }) {
    return this.put(`api/applications/${id}/reject`, body);
  }

  assignComplexBrigade(id: string, body: { complex_id: string; comment: string }) {
    return this.put(`api/applications/${id}/brigade`, body);
  }

  getAgroDoc(id: string): Observable<{ code: string; document_id: string }> {
    return this.post(`api/integrations/agro-docs/get-document/${id}`);
  }

  getNearbyLands(geometry: { type: string; coordinates: any[] }): Observable<NearbyLand[]> {
    return this.post<NearbyLand[]>('api/integrations/cabinet-api/nearby-lands', geometry);
  }

  getLandDetails(id: string): Observable<LandDetail[]> {
    return this.get<LandDetail[]>(`api/integrations/cabinet-api/lands/${id}`);
  }
}
